/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/ScheduleImportModal.tsx
 * PURPOSE: Luồng NHẬP lịch — modal [17] "Nhập lịch làm việc" → modal [18] "Xem trước nhập"
 *          SC-009-SCHEDULE-GRID (Release v0 WP-6b)
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-29
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Nhập lịch tuần từ tệp .xlsx/.csv (preview → commit)
 * - Impact: Data Integrity — chỉ nhập ô hợp lệ, bỏ qua ô lỗi (4 lý do verbatim)
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (2-step modal, input[type=file] THẬT)
 * - Pattern: POST import/preview (multipart) → lưới đọc-chỉ NV × 7 ngày +
 *   Chi tiết lỗi per-dòng → POST import/commit (valid_assignments nguyên vẹn)
 * - Dependencies: useImportSchedulePreview, useImportScheduleCommit, scheduleShared
 *
 * @cross-references
 * - Mockup: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/grid-import.html [17][18]
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py (import/preview + import/commit)
 * ===============================================================================
 */

import React, { useMemo, useRef, useState } from "react";
import {
  useImportScheduleCommit,
  useImportSchedulePreview,
} from "../../../hooks/useManagerSchedule";
import type { ImportCommitResult, ImportPreviewData, ImportPreviewEmployee } from "../../../services/api/managerApi";
import { extractApiError, formatWeekRange } from "./scheduleShared";
import styles from "../ManagerSchedulePage.module.css";

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

interface ScheduleImportModalProps {
  weekStart: string;        // YYYY-MM-DD tuần đang xem
  weekNumber: number;       // Tuần ISO (nhãn [17])
  onClose: () => void;
  /** BUG THẬT đã sửa (2026-08-09): trước đây chỉ truyền `imported`, bỏ qua
   * `skipped`/`skipped_reasons` backend đã trả sẵn — Manager thấy "Đã nhập
   * 15" (thay vì 20 dòng preview hợp lệ) không rõ vì sao 5 dòng biến mất.
   * Truyền cả object để page dựng toast đầy đủ lý do. */
  onImported: (result: ImportCommitResult) => void;
}

const ScheduleImportModal: React.FC<ScheduleImportModalProps> = ({
  weekStart,
  weekNumber,
  onClose,
  onImported,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreviewData | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);

  const previewMutation = useImportSchedulePreview();
  const commitMutation = useImportScheduleCommit();

  // [18] Gom hàng preview theo khu vực (giữ thứ tự server: trong scope → ngoài scope)
  const previewGroups = useMemo(() => {
    if (!preview) return [];
    const groups: Array<{ name: string; employees: ImportPreviewEmployee[] }> = [];
    preview.preview_grid.forEach((emp) => {
      const name = emp.work_area_name ?? "";
      let group = groups.find((g) => g.name === name);
      if (!group) {
        group = { name, employees: [] };
        groups.push(group);
      }
      group.employees.push(emp);
    });
    return groups;
  }, [preview]);

  // Mẫu CSV dựng ngay ở client — đúng tên cột chuẩn khớp bộ alias backend
  // (_match_import_header), kèm 1 dòng ví dụ. Không cần gọi API vì đây chỉ
  // là khung cột tĩnh, không phải dữ liệu thật của tuần.
  const handleDownloadTemplate = () => {
    const csvContent =
      "﻿" + // BOM — Excel mở tiếng Việt không vỡ dấu (khớp cách export CSV của backend)
      "Nhân viên,Ngày,Mã ca,Bộ phận\n" +
      "NV001,15/07/2026,M1,Phục vụ\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mau-nhap-lich.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setStepError(null);
    onClose();
  };

  const handlePreview = () => {
    if (!file || previewMutation.isPending) return;
    setStepError(null);
    previewMutation.mutate(
      { file, weekStart },
      {
        onSuccess: (data) => setPreview(data),
        onError: (error) => setStepError(extractApiError(error).message),
      },
    );
  };

  const handleCommit = () => {
    if (!preview || commitMutation.isPending || preview.valid_count === 0) return;
    commitMutation.mutate(
      { weekStart, assignments: preview.valid_assignments },
      {
        onSuccess: (result) => {
          handleClose();
          onImported(result);
        },
        onError: (error) => setStepError(extractApiError(error).message),
      },
    );
  };

  // ══════════ [17] MODAL "Nhập lịch làm việc" — bước 1 chọn tệp ══════════
  if (!preview) {
    return (
      <div className={styles.modalOverlay}>
        <div className={`${styles.modal} ${styles.modalImport}`} role="dialog" aria-modal="true" aria-labelledby="importTitle">
          <div className={styles.modalHead}>
            <h2 className={styles.modalTitle} id="importTitle">
              Nhập lịch làm việc
            </h2>
            <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={handleClose}>
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalBody}>
            {stepError && (
              <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
                {stepError}
              </div>
            )}
            <p>Chọn tệp .xlsx hoặc .csv</p>
            <div className={styles.filePick}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                hidden
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setStepError(null);
                }}
              />
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => fileInputRef.current?.click()}
              >
                Chọn tệp…
              </button>
              <span className={styles.filePickName}>{file ? file.name : ""}</span>
            </div>
            <p className={`${styles.muted} ${styles.txtSm}`}>
              Cột yêu cầu: Nhân viên (mã hoặc tên) · Ngày (dd/MM/yyyy) · Mã ca · (tùy) Bộ phận
            </p>
            <p className={`${styles.muted} ${styles.txtSm}`} style={{ marginTop: "var(--nqh-space-2)" }}>
              Lịch nhập áp cho tuần đang xem (Tuần {weekNumber}).
            </p>
            {/* Thêm 2026-08-09: mẫu file đúng tên cột chuẩn — tránh lặp lại
                lỗi "Thiếu cột bắt buộc" vì tự đặt tên cột khác đi. */}
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary} ${styles.txtSm}`}
              style={{ marginTop: "var(--nqh-space-2)" }}
              onClick={handleDownloadTemplate}
            >
              Tải mẫu Excel (.csv)
            </button>
          </div>
          <div className={styles.modalFoot}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={handleClose}>
              Hủy
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={!file || previewMutation.isPending}
              onClick={handlePreview}
            >
              {previewMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
              Xem trước
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ══════════ [18] MODAL "Xem trước nhập" — lưới NV × 7 ngày ĐỌC-CHỈ ══════════
  const errorRows = preview.rows.filter((row) => !row.valid);
  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" aria-labelledby="previewTitle">
        <div className={styles.modalHead}>
          <h2 className={styles.modalTitle} id="previewTitle">
            Xem trước nhập — Tuần {weekNumber} · {formatWeekRange(preview.week_start, preview.week_end)}
            {preview.branch_code ? ` · ${preview.branch_code}` : ""}
          </h2>
          <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          {stepError && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
              {stepError}
            </div>
          )}
          <div className={styles.gridWrap}>
            <table className={styles.grid} aria-label={`Xem trước nhập tuần ${weekNumber} · ${formatWeekRange(preview.week_start, preview.week_end)}`}>
              <thead>
                <tr>
                  <th className={styles.colEmp} scope="col">Nhân viên</th>
                  {preview.preview_grid[0]?.days.map((day) => (
                    <th key={day.date} className={styles.colDay} scope="col">
                      {day.day_label} <small>{day.date.split("-")[2]}</small>
                    </th>
                  )) ?? (
                    <th className={styles.colDay} scope="col">—</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {previewGroups.map((group, gi) => (
                  <React.Fragment key={group.name || `group-${gi}`}>
                    <tr className={styles.deptGroup}>
                      <th colSpan={8} scope="colgroup">
                        <span className={styles.deptToggle}>
                          <span className={styles.deptToggleChev} aria-hidden="true">
                            <ChevronDownIcon />
                          </span>
                          {group.name}
                        </span>
                      </th>
                    </tr>
                    {group.employees.map((emp, ei) => (
                      <tr key={emp.user_id ?? `out-${gi}-${ei}`}>
                        <th className={styles.colEmp} scope="row">
                          <span className={styles.empName}>
                            {emp.out_of_scope && (
                              <span className={styles.importErrorMark} aria-hidden="true">✖ </span>
                            )}
                            {emp.full_name}
                          </span>
                        </th>
                        {emp.days.map((day) => (
                          <td key={day.date}>
                            <span
                              className={`${styles.shiftCell} ${styles.shiftCellReadonly} ${
                                day.cells.length === 0 ? styles.shiftCellEmpty : ""
                              } ${day.cells.some((c) => !c.valid) ? styles.shiftCellImportError : ""}`}
                            >
                              {day.cells.length === 0 ? (
                                <span className={styles.shiftCellDash} aria-hidden="true" />
                              ) : (
                                day.cells.map((cell, ci) => (
                                  <span key={ci}>
                                    {!cell.valid && (
                                      <span className={styles.importErrorMark} aria-hidden="true">✖</span>
                                    )}
                                    <span className={styles.shiftChip}>{cell.shift_code}</span>
                                  </span>
                                ))
                              )}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Dòng tổng hợp (verbatim server) */}
          <p className={styles.importSummary}>{preview.summary}</p>

          {errorRows.length > 0 && (
            <>
              <p className={styles.importSummary} style={{ fontWeight: "var(--nqh-weight-semibold)" }}>
                Chi tiết lỗi:
              </p>
              <ul className={styles.importErrors}>
                {errorRows.map((row) => (
                  <li key={row.row_number}>
                    Dòng {row.row_number} · {row.staff_input} · {row.date_input} · {row.shift_code_input} — {row.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className={`${styles.muted} ${styles.txtSm}`} style={{ marginTop: "var(--nqh-space-2)" }}>
            Lý do lỗi: Nhân viên không thuộc scope · Mã ca không tồn tại · Ngày ngoài tuần · Vi phạm khoảng cách 2 ca
          </p>
        </div>
        <div className={styles.modalFoot}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={handleClose}>
            Hủy
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={preview.valid_count === 0 || commitMutation.isPending}
            onClick={handleCommit}
          >
            {commitMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
            Nhập {preview.valid_count} gán hợp lệ
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleImportModal;
